import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';

const ExamCellForms = ({ onNavigate, userInfo }) => {
    // ====================================================================================================
    // STATE MANAGEMENT
    // ====================================================================================================
    const [isMobile, setIsMobile] = useState(false);
    const [loading, setLoading] = useState(true);

    // Filter States
    const [activeTab, setActiveTab] = useState('all'); // all, pending, processing, completed, rejected
    const [formTypeFilter, setFormTypeFilter] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');

    // Modal States
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [adminRemarks, setAdminRemarks] = useState('');
    const [processingAction, setProcessingAction] = useState(false);

    // Notification
    const [notification, setNotification] = useState(null);

    // ====================================================================================================
    // MOCK DATA
    // ====================================================================================================
    const formTypes = [
        'All', 'Bonafide Certificate', 'Transcript', 'Migration Certificate', 'Duplicate Marksheet', 'Degree Certificate'
    ];

    const [requests, setRequests] = useState([
        {
            id: 'REQ-2026-001',
            studentId: 'STU001',
            studentName: 'Rahul Kumar',
            course: 'BCA',
            semester: '3rd',
            type: 'Bonafide Certificate',
            requestDate: '2026-01-16T09:00:00',
            status: 'Pending',
            paymentStatus: 'Paid',
            amount: 100,
            purpose: 'For Education Loan Application',
            documents: ['id_card.jpg'],
            remarks: ''
        },
        {
            id: 'REQ-2026-002',
            studentId: 'STU005',
            studentName: 'Vikram Singh',
            course: 'MCA',
            semester: '3rd',
            type: 'Transcript',
            requestDate: '2026-01-15T14:30:00',
            status: 'Processing',
            paymentStatus: 'Paid',
            amount: 500,
            purpose: 'Higher Studies Abroad',
            documents: ['marksheets_all.pdf'],
            remarks: 'Verification in progress.'
        },
        {
            id: 'REQ-2026-003',
            studentId: 'STU002',
            studentName: 'Priya Sharma',
            course: 'BCA',
            semester: '3rd',
            type: 'Migration Certificate',
            requestDate: '2026-01-12T11:20:00',
            status: 'Completed',
            paymentStatus: 'Paid',
            amount: 300,
            purpose: 'Transfer to another university',
            documents: ['transfer_cert.pdf'],
            remarks: 'Certificate issued on 14/01/2026'
        },
        {
            id: 'REQ-2026-004',
            studentId: 'STU012',
            studentName: 'Ananya Das',
            course: 'MBA',
            semester: '4th',
            type: 'Duplicate Marksheet',
            requestDate: '2026-01-10T10:00:00',
            status: 'Rejected',
            paymentStatus: 'Pending',
            amount: 200,
            purpose: 'Lost original',
            documents: ['fir_copy.jpg'],
            remarks: 'Payment not received.'
        },
        // Generating more data
        ...Array.from({ length: 20 }).map((_, i) => ({
            id: `REQ-2026-0${i + 5}`,
            studentId: `STU0${i + 10}`,
            studentName: `Student ${i + 1}`,
            course: i % 3 === 0 ? 'BCA' : 'MCA',
            semester: `${(i % 6) + 1}th`,
            type: formTypes[(i % 5) + 1],
            requestDate: `2026-01-${10 + (i % 5)}T10:00:00`,
            status: ['Pending', 'Processing', 'Completed', 'Rejected'][i % 4],
            paymentStatus: i % 4 === 3 ? 'Pending' : 'Paid',
            amount: 100 * ((i % 5) + 1),
            purpose: 'General purpose',
            documents: [],
            remarks: ''
        }))
    ]);

    // ====================================================================================================
    // EFFECTS & HELPERS
    // ====================================================================================================
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1024);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        setTimeout(() => setLoading(false), 800);
    }, []);

    const getStatusStyle = (status) => {
        switch (status) {
            case 'Completed': return { bg: '#c6f6d5', color: '#22543d', border: '#9ae6b4' };
            case 'Processing': return { bg: '#bee3f8', color: '#2a4365', border: '#90cdf4' };
            case 'Rejected': return { bg: '#fed7d7', color: '#822727', border: '#feb2b2' };
            default: return { bg: '#feebc8', color: '#744210', border: '#fbd38d' };
        }
    };

    // ====================================================================================================
    // ACTIONS
    // ====================================================================================================
    const handleViewDetails = (req) => {
        setSelectedRequest(req);
        setAdminRemarks(req.remarks || '');
        setIsDetailModalOpen(true);
    };

    const updateRequestStatus = (newStatus) => {
        if (!selectedRequest) return;
        setProcessingAction(true);

        setTimeout(() => {
            const updatedList = requests.map(r =>
                r.id === selectedRequest.id
                    ? { ...r, status: newStatus, remarks: adminRemarks }
                    : r
            );
            setRequests(updatedList);
            setProcessingAction(false);
            setIsDetailModalOpen(false);

            setNotification({
                message: `Request ID ${selectedRequest.id} marked as ${newStatus}`,
                type: 'success'
            });
            setTimeout(() => setNotification(null), 3000);
        }, 800);
    };

    // ====================================================================================================
    // FILTERING
    // ====================================================================================================
    const filteredRequests = requests.filter(req => {
        const matchesSearch =
            req.studentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            req.studentId.toLowerCase().includes(searchQuery.toLowerCase()) ||
            req.id.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesType = formTypeFilter === 'All' || req.type === formTypeFilter;
        const matchesTab = activeTab === 'all' || req.status.toLowerCase() === activeTab;

        return matchesSearch && matchesType && matchesTab;
    });

    // ====================================================================================================
    // UI COMPONENTS
    // ====================================================================================================

    // Detail Modal
    const DetailModal = () => {
        if (!selectedRequest) return null;
        const statusStyle = getStatusStyle(selectedRequest.status);

        return (
            <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.6)', zIndex: 1100,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backdropFilter: 'blur(3px)'
            }}>
                <div style={{
                    background: '#fff', width: '100%', maxWidth: '800px',
                    borderRadius: '16px', boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
                    maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column'
                }}>
                    <div style={{
                        padding: '1.5rem', borderBottom: '1px solid #eee',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        background: '#f8f9fa'
                    }}>
                        <h2 style={{ margin: 0, color: '#2d3748' }}>Request Details: <span style={{ fontSize: '0.9em', color: '#718096' }}>{selectedRequest.id}</span></h2>
                        <button onClick={() => setIsDetailModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#718096' }}>✕</button>
                    </div>

                    <div style={{ padding: '2rem', flex: 1 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
                            <div>
                                <h3 style={{ color: '#238548', marginBottom: '1rem', borderBottom: '2px solid #e2e8f0', paddingBottom: '0.5rem' }}>Student Information</h3>
                                <p><strong>Name:</strong> {selectedRequest.studentName}</p>
                                <p><strong>ID:</strong> {selectedRequest.studentId}</p>
                                <p><strong>Course:</strong> {selectedRequest.course} ({selectedRequest.semester} Sem)</p>
                            </div>
                            <div>
                                <h3 style={{ color: '#238548', marginBottom: '1rem', borderBottom: '2px solid #e2e8f0', paddingBottom: '0.5rem' }}>Request Information</h3>
                                <p><strong>Type:</strong> {selectedRequest.type}</p>
                                <p><strong>Date:</strong> {new Date(selectedRequest.requestDate).toLocaleDateString()}</p>
                                <p><strong>Payment:</strong> <span style={{
                                    color: selectedRequest.paymentStatus === 'Paid' ? '#238548' : '#e53e3e',
                                    fontWeight: 'bold',
                                    background: selectedRequest.paymentStatus === 'Paid' ? '#f0fdf4' : '#fff5f5',
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.9rem'
                                }}>{selectedRequest.paymentStatus} (₹{selectedRequest.amount})</span></p>
                            </div>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <p><strong>Purpose:</strong> {selectedRequest.purpose}</p>
                            <p className="status-label"><strong>Current Status:</strong>
                                <span style={{
                                    padding: '0.2rem 0.6rem', borderRadius: '4px', marginLeft: '0.5rem',
                                    fontSize: '0.9rem', fontWeight: 'bold', backgroundColor: statusStyle.bg,
                                    color: statusStyle.color, border: `1px solid ${statusStyle.border}`
                                }}>{selectedRequest.status}</span>
                            </p>
                        </div>

                        <div style={{ background: '#f7fafc', padding: '1.5rem', borderRadius: '8px' }}>
                            <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', color: '#4a5568' }}>Administrative Action</h3>
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Remarks / Tracking Details</label>
                                <textarea
                                    value={adminRemarks}
                                    onChange={(e) => setAdminRemarks(e.target.value)}
                                    placeholder="Enter reference number, courier details, or rejection reason..."
                                    style={{ width: '100%', padding: '0.8rem', borderRadius: '6px', border: '1px solid #cbd5e0', minHeight: '80px', boxSizing: 'border-box' }}
                                />
                            </div>
                            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                                <button
                                    onClick={() => updateRequestStatus('Processing')}
                                    disabled={selectedRequest.status === 'Processing' || processingAction}
                                    style={{ padding: '0.6rem 1.2rem', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', background: '#4299e1', opacity: selectedRequest.status === 'Processing' ? 0.6 : 1 }}
                                >
                                    Mark In-Progress
                                </button>
                                <button
                                    onClick={() => updateRequestStatus('Rejected')}
                                    disabled={selectedRequest.status === 'Rejected' || processingAction}
                                    style={{ padding: '0.6rem 1.2rem', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', background: '#e53e3e', opacity: selectedRequest.status === 'Rejected' ? 0.6 : 1 }}
                                >
                                    Reject Request
                                </button>
                                <button
                                    onClick={() => updateRequestStatus('Completed')}
                                    disabled={selectedRequest.status === 'Completed' || processingAction}
                                    style={{ padding: '0.6rem 1.2rem', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', background: '#238548', opacity: selectedRequest.status === 'Completed' ? 0.6 : 1 }}
                                >
                                    Complete & Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ====================================================================================================
    // MAIN RENDER
    // ====================================================================================================
    return (
        <Layout role="examcell" onNavigate={onNavigate} userInfo={userInfo} activePage="examcell-forms">

            {/* Header Controls */}
            <div style={{ marginBottom: '2rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.8rem', color: '#2d3748' }}>Student Request Forms</h2>
                <p style={{ margin: '0.5rem 0 0', color: '#718096' }}>Manage Bonafide, Transcripts, Migration Certificates, etc.</p>
            </div>

            {/* Notification */}
            {notification && (
                <div style={{
                    position: 'fixed', top: '160px', right: '20px', zIndex: 2000,
                    background: '#238548', color: '#fff', padding: '1rem 1.5rem', borderRadius: '8px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.2)', animation: 'slideLeft 0.3s ease'
                }}>
                    {notification.message}
                    <style>{`@keyframes slideLeft { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
                </div>
            )}

            {/* Stats & Actions */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                {[
                    { label: 'Total Requests', value: requests.length, color: '#2b6cb0', borderColor: '#63b3ed' },
                    { label: 'Pending Action', value: requests.filter(r => r.status === 'Pending').length, color: '#ed8936', borderColor: '#f6ad55' },
                    { label: 'Processing', value: requests.filter(r => r.status === 'Processing').length, color: '#4299e1', borderColor: '#63b3ed' },
                    { label: 'Completed', value: requests.filter(r => r.status === 'Completed').length, color: '#238548', borderColor: '#48bb78' }
                ].map((stat, i) => (
                    <div key={i} style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.02)', borderTop: `4px solid ${stat.borderColor}`, textAlign: 'center' }}>
                        <h3 style={{ margin: 0, fontSize: '2.5rem', color: stat.color }}>{stat.value}</h3>
                        <p style={{ margin: '0.5rem 0 0 0', color: '#718096', fontWeight: '500' }}>{stat.label}</p>
                    </div>
                ))}
            </div>

            {/* Controls */}
            <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', marginBottom: '2rem', display: 'flex', gap: '1.5rem', flexWrap: 'wrap', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
                <div style={{ flex: 1, minWidth: '250px' }}>
                    <label style={{ display: 'block', color: '#4a5568', fontSize: '0.9rem', marginBottom: '0.5rem', fontWeight: '500' }}>Search</label>
                    <input
                        type="text" placeholder="Student ID, Name, Request ID..."
                        value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box' }}
                        onFocus={(e) => e.target.style.borderColor = '#238548'}
                        onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                    />
                </div>
                <div style={{ width: '250px', flexGrow: 1 }}>
                    <label style={{ display: 'block', color: '#4a5568', fontSize: '0.9rem', marginBottom: '0.5rem', fontWeight: '500' }}>Filter by Type</label>
                    <select
                        value={formTypeFilter} onChange={(e) => setFormTypeFilter(e.target.value)}
                        style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid #e2e8f0', outline: 'none', cursor: 'pointer', boxSizing: 'border-box' }}
                    >
                        {formTypes.map((t, i) => <option key={i} value={t}>{t}</option>)}
                    </select>
                </div>
            </div>

            {/* Tabs */}
            <div style={{ marginBottom: '1.5rem', borderBottom: '2px solid #e2e8f0', display: 'flex', overflowX: 'auto', paddingBottom: '0' }}>
                {['all', 'pending', 'processing', 'completed', 'rejected'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        style={{
                            padding: '0.75rem 1.5rem', background: 'none', border: 'none',
                            borderBottom: activeTab === tab ? '3px solid #238548' : '3px solid transparent',
                            color: activeTab === tab ? '#238548' : '#718096', fontWeight: activeTab === tab ? '600' : '500',
                            textTransform: 'capitalize', cursor: 'pointer', whiteSpace: 'nowrap', marginBottom: '-2px',
                            fontSize: '1rem', transition: 'all 0.2s'
                        }}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {/* Data Table */}
            <div style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                            <tr>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'left', color: '#64748b', fontWeight: '600', fontSize: '0.85rem', textTransform: 'uppercase' }}>ID</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'left', color: '#64748b', fontWeight: '600', fontSize: '0.85rem', textTransform: 'uppercase' }}>Student</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'left', color: '#64748b', fontWeight: '600', fontSize: '0.85rem', textTransform: 'uppercase' }}>Request Type</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'left', color: '#64748b', fontWeight: '600', fontSize: '0.85rem', textTransform: 'uppercase' }}>Date</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'center', color: '#64748b', fontWeight: '600', fontSize: '0.85rem', textTransform: 'uppercase' }}>Payment</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'center', color: '#64748b', fontWeight: '600', fontSize: '0.85rem', textTransform: 'uppercase' }}>Status</th>
                                <th style={{ padding: '1rem 1.5rem', textAlign: 'center', color: '#64748b', fontWeight: '600', fontSize: '0.85rem', textTransform: 'uppercase' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" style={{ padding: '3rem', textAlign: 'center', color: '#666' }}>Loading requests...</td></tr>
                            ) : filteredRequests.length === 0 ? (
                                <tr><td colSpan="7" style={{ padding: '3rem', textAlign: 'center', color: '#718096' }}>No requests found.</td></tr>
                            ) : (
                                filteredRequests.map(req => {
                                    const statusStyle = getStatusStyle(req.status);
                                    return (
                                        <tr key={req.id} style={{ borderBottom: '1px solid #edf2f7', transition: 'background 0.2s' }}>
                                            <td style={{ padding: '1rem 1.5rem', fontWeight: '500', fontSize: '0.9rem', color: '#2d3748' }}>{req.id}</td>
                                            <td style={{ padding: '1rem 1.5rem' }}>
                                                <div style={{ fontWeight: '600', color: '#2d3748', fontSize: '0.95rem' }}>{req.studentName}</div>
                                                <div style={{ fontSize: '0.85rem', color: '#718096', marginTop: '0.25rem' }}>{req.studentId}</div>
                                            </td>
                                            <td style={{ padding: '1rem 1.5rem', fontSize: '0.9rem', color: '#2d3748' }}>{req.type}</td>
                                            <td style={{ padding: '1rem 1.5rem', fontSize: '0.9rem', color: '#4a5568' }}>{new Date(req.requestDate).toLocaleDateString()}</td>
                                            <td style={{ padding: '1rem 1.5rem', textAlign: 'center' }}>
                                                <span style={{
                                                    color: req.paymentStatus === 'Paid' ? '#238548' : '#e53e3e',
                                                    fontWeight: '600', fontSize: '0.85rem',
                                                    background: req.paymentStatus === 'Paid' ? '#f0fdf4' : '#fff5f5',
                                                    padding: '0.2rem 0.6rem', borderRadius: '4px'
                                                }}>
                                                    {req.paymentStatus}
                                                </span>
                                            </td>
                                            <td style={{ padding: '1rem 1.5rem', textAlign: 'center' }}>
                                                <span style={{
                                                    padding: '0.3rem 0.8rem', borderRadius: '20px', fontSize: '0.85rem',
                                                    fontWeight: '600', backgroundColor: statusStyle.bg,
                                                    color: statusStyle.color, border: `1px solid ${statusStyle.border}`
                                                }}>
                                                    {req.status}
                                                </span>
                                            </td>
                                            <td style={{ padding: '1rem 1.5rem', textAlign: 'center' }}>
                                                <button
                                                    onClick={() => handleViewDetails(req)}
                                                    style={{
                                                        padding: '0.5rem 1rem', background: '#edf2f7', border: 'none',
                                                        borderRadius: '6px', cursor: 'pointer', color: '#2d3748', fontWeight: '500',
                                                        fontSize: '0.9rem', transition: 'all 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.background = '#e2e8f0'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.background = '#edf2f7'; }}
                                                >
                                                    Manage
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {isDetailModalOpen && <DetailModal />}
        </Layout>
    );
};

export default ExamCellForms;
